import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IEmployee } from "../../domain/IEmployee";
import { IEmployeeDetails } from "../../domain/IEmployeeDetails";
import { IGetAllEmployeesRequestParams } from "../../domain/IGetAllEmployeesRequestParams";
import { hrService } from "../../services/hr.services";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { getIndexById, getNextSortOrder, ISortParams, sort } from "utils/sortUtils";
import { IUpdateEmployeeRequestParams } from "../../domain/IUpdateEmployeeRequestParams";

export const getEmployees = createAsyncThunk(
  "HumanResources/getEmployees",
  async (data: IGetAllEmployeesRequestParams) => {
    try {
      return await hrService.getAllEmployees(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getEmployeeDetails = createAsyncThunk(
  "HumanResources/getEmployeeDetails",
  async (employeeId: number) => {
    try {
      return await hrService.getEmployeeDetails(employeeId);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateEmployeeDetails = createAsyncThunk(
  "HumanResources/updateEmployeeDetails",
  async (data: IUpdateEmployeeRequestParams) => {
    try {
      return await hrService.updateEmployee(data);
    } catch (err) {
      setError(err);
    }
  }
);


interface employeeState {
  loading: boolean;
  list: IEmployee[];
  sort: ISortParams | undefined;
  employeeDetails?: IEmployeeDetails;
  employeeDetailsLoading: boolean;
  selectedIndex: number;
  error: {};
  newEmployeePopover: IPopoverState;
  editModalOpen: boolean;
  searchPopoverOpen: IPopoverState;
  indexForOperation: number;
  deleteEmployeeModalOpen: boolean;
  getEmployeesParams: IGetAllEmployeesRequestParams;
}

const initialState: employeeState = {
  loading: false,
  list: [],
  sort: undefined,
  employeeDetails: undefined,
  employeeDetailsLoading: true,
  selectedIndex: 0,
  error: {},
  newEmployeePopover: { isOpen: false },
  editModalOpen: false,
  searchPopoverOpen: { isOpen: false },
  indexForOperation: -1,
  deleteEmployeeModalOpen: false,
  getEmployeesParams: {},
};

export const employeeSlice = createSlice({
  name: "human-resources",
  initialState: initialState,
  reducers: {
    updateEmployeeTemporarily: (state, action: PayloadAction<IEmployee>) => {
      state.list = state.list.map((item) => {
        if (item.employeeId === action.payload.employeeId)
          return action.payload;
        return item;
      });
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setSort: (state, action: PayloadAction<ISortParams>) => {
      const newSortOrder = getNextSortOrder(state.sort, action.payload.key);
      const newSortParams = {
        key: action.payload.key,
        typeOfValue: action.payload.typeOfValue,
        order: newSortOrder
      };
      let selectedEmployeeId = undefined;
      if (state.list.length > 0) {
        selectedEmployeeId = state.list[state.selectedIndex].employeeId;
      }
      state.sort = newSortParams;
      state.list = sort(state.list, newSortParams);
      state.selectedIndex = getIndexById(
        state.list,
        selectedEmployeeId,
        (list: IEmployee) => list.employeeId);
    },
    setNewEmployeePopover: (state, action: PayloadAction<IPopoverState>) => {
      state.newEmployeePopover = action.payload;
    },
    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    setSearchPopoverOpen: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopoverOpen = action.payload;
    },
    setEditModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.editModalOpen = action.payload.visible;
      state.indexForOperation = action.payload.index;
    },
    setDeleteEmployeeModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deleteEmployeeModalOpen = action.payload.visible;
      state.indexForOperation = action.payload.index;
    },
    updateEmployeeDetailsTemporarily: (state, action: PayloadAction<IEmployeeDetails>) => {
      state.employeeDetailsLoading = false;
      state.employeeDetails = action.payload;
      if (action.payload !== undefined) {
        const details = action.payload;
        state.list[state.selectedIndex] = {
          ...state.list[state.selectedIndex],
          salaryType: details.salaryType,
          notes: details.notes,
          status: details.status,
          employedUntil: details.employedUntil,
          employeeName: details.employeeName,
          registered: details.registered,
          gender: details.gender,
          employeeRole: details.employeeRole,
        };
      }

    },
    setGetEmployeesParams: (state, action: PayloadAction<IGetAllEmployeesRequestParams>) => {
      state.getEmployeesParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getEmployees.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getEmployees.fulfilled, (state, action) => {
      state.loading = false;
      state.list = action.payload ? action.payload : [];
    });
    builder.addCase(getEmployees.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getEmployeeDetails.pending, (state) => {
      state.employeeDetailsLoading = true;
    });
    builder.addCase(getEmployeeDetails.fulfilled, (state, action) => {
      state.employeeDetailsLoading = false;
      state.employeeDetails = action.payload;
    });
    builder.addCase(getEmployeeDetails.rejected, (state, action) => {
      state.employeeDetailsLoading = false;
      state.error = action.error;
    });
    builder.addCase(updateEmployeeDetails.fulfilled, (state, action) => {
      state.employeeDetailsLoading = false;
      state.employeeDetails = action.payload;
      if (action.payload !== undefined) {
        const details = action.payload;
        state.list[state.selectedIndex] = {
          ...state.list[state.selectedIndex],
          salaryType: details.salaryType,
          notes: details.notes,
          status: details.status,
          employedUntil: details.employedUntil,
          employeeName: details.employeeName,
          registered: details.registered,
          gender: details.gender,
          employeeRole: details.employeeRole,
        };
      }
    });
  },
});

export const {
  setNewEmployeePopover,
  updateEmployeeTemporarily,
  setSelectedIndex,
  setEditModalOpen,
  setDeleteEmployeeModalOpen,
  setSearchPopoverOpen,
  setError,
  setSort,
  updateEmployeeDetailsTemporarily,
  setGetEmployeesParams,
} = employeeSlice.actions;

export const selectState = (state: RootState) => state.hr.employee;
export const selectEmployee = (state: RootState) =>
  state.hr.employee.list[state.hr.employee.selectedIndex];
export const selectEmployeeForOperation = (state: RootState) =>
  state.hr.employee.list[state.hr.employee.indexForOperation];

export default employeeSlice.reducer;
